/**
 * Which model in a GGUF repo, and which quantization of it.
 *
 * ## Why this is not `findComponents`
 *
 * A safetensors repo holds *models*, separated by directory — `vae/`, `unet/` —
 * and `components.ts` finds them by prefix. A GGUF repo holds one model many
 * times: `unsloth/DeepSeek-V4-Flash-0731-GGUF` carries 51 files across variant
 * directories, and `prism-ml/Ternary-Bonsai-27B-gguf` carries eight at the root
 * with nothing but the filename to tell `Q2_0` from `F16`. Prefix cannot
 * separate those, so a second finder is needed rather than a wider first one.
 *
 * ## A variant is a set of files, not a file
 *
 * llama.cpp splits large models into `…-00001-of-00003.gguf` parts, and each
 * part carries its own tensor index for the tensors it holds. Reading only the
 * first would report a third of the model as the whole of it — a confident wrong
 * inventory, and one that looks entirely plausible.
 *
 * So a variant names every part, and the cost of that is smaller than it sounds:
 * the tokenizer sits only in the first part, so a three-part variant pays the
 * ~10 MiB header once and kilobytes twice.
 *
 * ## Auxiliary files are not variants
 *
 * `mmproj-*.gguf` is a vision projector, `imatrix.gguf` is calibration data, and
 * neither is the model. Excluding them is not tidiness: picking the shortest
 * filename selected `mmproj-F16.gguf` twice during scoping and produced real
 * measurements of the wrong file, which is the failure mode this project keeps
 * meeting. A third form of it — `antirez/deepseek-v4-gguf`'s MTP-support
 * sidecar, a genuine model file that is not *the* model — is not caught here and
 * is filed in plan §19c; it needs the parsed metadata, which discovery does not
 * have.
 */

import type { RepoComponent } from "../hf/components.js";

export interface GgufVariant {
  /**
   * Stable identity for this variant — the deep link's `?part=` value and the
   * cache key. The first part's path, which is unique within a repo and
   * survives a re-listing in a way an index never would.
   */
  readonly key: string;
  /** What to show a visitor: `Q4_K_M`, `UD-IQ1_M/Q2_K`. */
  readonly label: string;
  /** Every file in this variant, in part order. One entry unless it is split. */
  readonly files: readonly string[];
}

function dirOf(path: string): string {
  const cut = path.lastIndexOf("/");
  return cut === -1 ? "" : path.slice(0, cut + 1);
}

/**
 * Markers that appear anywhere in a name, because publishers put them anywhere.
 *
 * `mmproj` is the vision projector. An earlier version anchored this on `^` or
 * `/`, and `Ternary-Bonsai-27B-mmproj-BF16.gguf` therefore reached the picker as
 * though a vision projector were a quantization of the model.
 */
const AUXILIARY_ANYWHERE = /\b(?:mmproj|lora)\b/i;

/**
 * Markers that only count at the **start** of a filename, and the distinction is
 * not pedantry — it was wrong in the other direction first.
 *
 * `imatrix.gguf` and `imatrix_unsloth.gguf` are calibration data. But
 * `…-Q4KExperts-…-chat-v2-imatrix.gguf` in `antirez/deepseek-v4-gguf` is a real
 * model, quantized *using* an importance matrix — the word is a provenance note,
 * not a file kind. Matching `imatrix` anywhere excluded three genuine variants
 * from that repo's picker, which is the same class of error as letting `mmproj`
 * in and simply points the other way.
 *
 * The separator is spelled out rather than left to `\b`, because `\b` does not
 * fire between `imatrix` and `_` — both are word characters — so
 * `imatrix_unsloth.gguf` passed straight through the first version of this. The
 * decoy suite caught it on the run it was written in.
 */
const AUXILIARY_PREFIX = /^(?:imatrix|imat|vocab)(?:[-_.]|$)/i;

function isAuxiliaryPath(path: string): boolean {
  const base = path.slice(dirOf(path).length);
  return AUXILIARY_ANYWHERE.test(path) || AUXILIARY_PREFIX.test(base);
}

/** `…-00002-of-00005.gguf`. Captures the part number and the total. */
const SPLIT_PART = /-(\d{5})-of-(\d{5})\.gguf$/;

/**
 * A readable name for a variant, from its path.
 *
 * The filename usually carries the quantization's *name* (`…-Q4_K_M.gguf`) and
 * the directory sometimes carries it instead (`UD-IQ1_M/…`), so both are used
 * and the model's own name — repeated in every file in the repo — is dropped
 * where it can be identified.
 *
 * **A name, and nothing more.** A GGUF filename states a size target, not the
 * block formats inside: `bartowski/…-Q2_K.gguf` contains no `Q2_K` tensor at
 * all, and `unsloth/…-UD-Q2_K_XL.gguf` likewise, because llama.cpp reaches a
 * target by mixing formats per tensor role. So this returns a label to show,
 * never a claim to reason from — the dtypes a visitor sees come from the file's
 * own tensor index, via the per-dtype ledger. Measured in
 * `docs/evidence/gguf-quant-crosscheck.md`; pinned in the decoy suite.
 */
function labelFor(path: string, repoStem: string): string {
  const dir = dirOf(path).replace(/\/$/, "");
  let file = (path.slice(dir.length ? dir.length + 1 : 0) || path)
    .replace(SPLIT_PART, "")
    .replace(/\.gguf$/, "");
  if (repoStem && file.toLowerCase().startsWith(repoStem.toLowerCase())) {
    file = file.slice(repoStem.length).replace(/^[-_.]/, "");
  }
  if (dir && file) return `${dir}/${file}`;
  return file || dir || path;
}

/**
 * The longest prefix shared by every candidate filename, which is the model's
 * own name and is noise in every label.
 *
 * Derived rather than taken from the repo id: `prism-ml/Ternary-Bonsai-27B-gguf`
 * names its files `Ternary-Bonsai-27B-…`, which is close to the repo id but not
 * equal to it, and a mismatch would leave the noise in every label.
 */
function commonStem(names: readonly string[]): string {
  if (names.length < 2) return "";
  let stem = names[0] as string;
  for (const name of names.slice(1)) {
    let i = 0;
    while (i < stem.length && i < name.length && stem[i] === name[i]) i++;
    stem = stem.slice(0, i);
  }
  // Back off to a separator, so a stem never cuts a quant name in half.
  return stem.replace(/[^-_.]*$/, "").replace(/[-_.]$/, "");
}

/** Every readable variant in the repo, in a stable order. */
export function findGgufVariants(files: readonly string[]): GgufVariant[] {
  const candidates = files
    .filter((f) => f.endsWith(".gguf"))
    .filter((f) => !isAuxiliaryPath(f));
  if (candidates.length === 0) return [];

  /*
   * Split parts are grouped by everything except their part number, so
   * `x-00001-of-00003.gguf` and `x-00002-of-00003.gguf` become one variant and
   * a repo of 51 files does not present 51 choices.
   */
  const groups = new Map<string, string[]>();
  for (const file of candidates) {
    groups.set(file.replace(SPLIT_PART, ".gguf"), []);
  }
  for (const file of candidates) {
    (groups.get(file.replace(SPLIT_PART, ".gguf")) as string[]).push(file);
  }

  const stem = commonStem(
    [...groups.keys()].map((k) => k.slice(dirOf(k).length).replace(/\.gguf$/, "")),
  );

  return [...groups.entries()]
    .map(([key, parts]) => {
      // Part order matters: a later part's tensors continue the earlier one's,
      // and the index is not required to be listed in order.
      const ordered = [...parts].sort((a, b) => {
        const pa = SPLIT_PART.exec(a)?.[1] ?? "";
        const pb = SPLIT_PART.exec(b)?.[1] ?? "";
        return pa.localeCompare(pb) || a.localeCompare(b);
      });
      return {
        key: ordered[0] as string,
        label: labelFor(key, stem),
        files: ordered,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The variant to open without asking, or `undefined` when it is a real choice.
 *
 * Deliberately stricter than `autoComponent`: that one opens a root-level model
 * without asking, because a root `model.safetensors` *is* the model. Here every
 * variant may sit at the root and they are all equally the model — at different
 * precisions — so there is no "obvious" one and picking would be choosing on the
 * visitor's behalf between answers that differ by a factor of eight in size.
 *
 * One variant needs no question. More than one always does.
 */
export function autoGgufVariant(
  variants: readonly GgufVariant[],
): GgufVariant | undefined {
  return variants.length === 1 ? variants[0] : undefined;
}

/**
 * The band a variant's bits per **full-model parameter** must fall in to be a
 * quantization of this model at all.
 *
 * Not a quality measure and not comparable to the picker's `≈bpw` column: this
 * divides a variant's bytes by the *repo's* parameter total, so it answers "can
 * this file be N parameters at all", not "how good is it".
 *
 * **The floor.** The narrowest type ggml has is `Q1_0` — 18 bytes per 128
 * elements, **1.125 bpw** (`ggml-types.ts`), and `Q1_0` files exist in the wild;
 * `ggml-types.test.ts` records two uploaders'. So the margin to the format is
 * 0.125, not the 0.69 an earlier version of this comment claimed by citing
 * `TQ1_0`. 1.0 spends that margin on denominator slack: `gguf.total` is
 * whichever file the Hub parsed, and tied-vs-untied embeddings move the
 * denominator by more than rounding does.
 *
 * **The ceiling, and it is not symmetry for its own sake.** `F64`/`I64` at 64
 * bpw are the widest entries, so a variant computing *above* 64 proves the
 * denominator is wrong rather than the file — the Hub parsed some other model's
 * parameter count. That is not a fact about the variant, so it invalidates the
 * whole check rather than excluding one entry.
 */
const MIN_BITS_PER_PARAM = 1.0;
const MAX_BITS_PER_PARAM = 64;

/** What a repo's variant list is, once things that are not the model are out. */
export interface GgufVariantSet {
  readonly variants: readonly GgufVariant[];
  /**
   * Whether the exclusion could actually run. `false` when the Hub reported no
   * parameter total, in which case `variants` is the unchecked list and any
   * count or span taken from it is unverifiable — the caller must withhold the
   * claim rather than make it.
   */
  readonly verified: boolean;
  /** Excluded, with what they measured, so a caller can say why. */
  readonly excluded: readonly {
    readonly variant: GgufVariant;
    readonly bitsPerParam: number;
  }[];
}

/**
 * The repo's variants with the too-small-to-be-this-model files removed.
 *
 * **Discovery cannot do this and should not try.** `findGgufVariants` judges
 * filenames, and the sidecar class has no filename rule — `MTP` is as plausible
 * a quant marker as `MXFP4`, and `unsloth/Qwen3.6-27B-MTP-GGUF` is a whole repo
 * whose variants *are* the model. What discriminates is arithmetic against the
 * repo's own parameter total, which only a caller holding `fetchVariantSizes`'
 * answer has.
 *
 * The occasion: `unsloth/Qwen3.8-27B-GGUF` added `MTP/mtp-Qwen3.8-27B-Q4_0.gguf`
 * — 1.37 GB against a 27.3B-parameter model, **0.40 bits per parameter**. It is
 * a speculative-decoding head. Unfiltered it made the repo's share card read
 * `26 quantizations · … · 1.28 GiB – 50.9 GiB`, a size this repo does not offer,
 * and it moved the median off `Q4_K_M` by shifting the index. Shipped to
 * production on 2026-08-19 and caught by rendering the card and reading it.
 *
 * **This is exclusion, not selection.** It defines what the set of variants
 * *is*, upstream of any pick, so it belongs on both surfaces from one
 * definition — the picker's count and the card's summary must not disagree, and
 * the default is then chosen from the cleaned set for free. Which variant is the
 * default remains the generator's alone (`og/src/default-variant.ts`).
 *
 * **Not applied inside `findGgufVariants`, on purpose.** `chooseComponent`
 * resolves `?part=` against the unfiltered list, so a visitor who deep-linked to
 * an MTP head still reaches it — `auxiliaryReason`'s stated posture, that
 * someone who asked for one may have meant to. What changes is only what we
 * *offer* and what we *count*, which is how `mmproj` is already treated.
 *
 * Known gap, not engineered for: a *large* sidecar passes. A half-size draft at
 * 4 bits computes ~2.0 against full parameters and survives this. Drafts
 * overwhelmingly ship as their own repos and MTP heads are small, so the
 * exposure is narrow; `auxiliaryReason` still guards the one variant actually
 * read, which is the map's subject.
 */
export function modelVariants(
  variants: readonly GgufVariant[],
  sizes: ReadonlyMap<string, number>,
  params: number | undefined,
): GgufVariantSet {
  if (!params || params <= 0) return { variants, verified: false, excluded: [] };

  const kept: GgufVariant[] = [];
  const excluded: { variant: GgufVariant; bitsPerParam: number }[] = [];
  const measured: number[] = [];
  for (const variant of variants) {
    // Summed across parts: BF16's second shard alone is 1.37 bits per parameter
    // and would be excluded as a file, where the variant it belongs to is 16.0.
    let bytes = 0;
    let known = true;
    for (const file of variant.files) {
      const size = sizes.get(file);
      if (size === undefined) {
        known = false;
        break;
      }
      bytes += size;
    }
    // No size is no evidence. Keeping it is the conservative direction here:
    // an unjudgeable variant stays in the list, and the callers that need a
    // total — the median, the span — already refuse on a missing size.
    if (!known) {
      kept.push(variant);
      continue;
    }
    const bitsPerParam = (bytes * 8) / params;
    measured.push(bitsPerParam);
    if (bitsPerParam >= MIN_BITS_PER_PARAM) kept.push(variant);
    else excluded.push({ variant, bitsPerParam });
  }

  /*
   * One variant wider than any ggml type means the denominator is not this
   * model's — the Hub parsed a different file for `gguf.total`. Every
   * measurement in this pass is then against the wrong number, including the
   * ones that passed, so the honest report is "could not check" rather than a
   * partial exclusion built on it.
   */
  if (measured.some((value) => value > MAX_BITS_PER_PARAM)) {
    return { variants, verified: false, excluded: [] };
  }

  return { variants: kept, verified: true, excluded };
}

/**
 * A variant as the picker's `RepoComponent`, so one UI serves both formats.
 *
 * `prefix` carries the variant key rather than a directory. That stretches the
 * field's stated contract — `${prefix}${file}` is a full path for safetensors —
 * and is safe only because the GGUF path never does that concatenation: a
 * variant names its files outright. Documented here rather than left to be
 * discovered, because the contract is written down two files away.
 */
export function variantAsComponent(variant: GgufVariant): RepoComponent {
  return {
    prefix: variant.key,
    label: variant.label,
    indexFile: undefined,
    singleFile: variant.files[0],
  };
}

/**
 * What was chosen and what was set aside, with a reason for each.
 *
 * Selection is a claim about *subject identity*, and an identity claim gets
 * established, verified, disclosed and regression-tested like any other
 * measurement (reviewer, 2026-08-09). This is the disclose half: a wrong subject
 * should be visible in the output rather than latent in it.
 *
 * The occasion for the rule was four separate instances in one day of a correct
 * measurement of the wrong subject — two `mmproj` picks, an `-F16` twin, and an
 * MTP-support sidecar. Every one of them would have been obvious in a line that
 * said which file was read.
 */
export function explainGgufSelection(files: readonly string[]): {
  readonly variants: readonly GgufVariant[];
  readonly excluded: readonly { readonly file: string; readonly reason: string }[];
} {
  const excluded = files
    .filter((f) => f.endsWith(".gguf") && isAuxiliaryPath(f))
    .map((file) => ({
      file,
      reason: AUXILIARY_ANYWHERE.test(file)
        ? "auxiliary: projector or adapter, not the model"
        : "auxiliary: calibration data, not a model",
    }));
  return { variants: findGgufVariants(files), excluded };
}

/**
 * Why a *read* variant is not the model, judged on facts rather than its name.
 *
 * The establish half of the same rule. Filenames are a convention we
 * pattern-match and have now got wrong in both directions; `general.architecture`
 * and the tensor count are facts the file states about itself.
 *
 * `antirez/deepseek-v4-gguf` ships `DeepSeek-V4-Flash-MTP-Q4K-Q8_0-F32.gguf`,
 * which declares `deepseek4_mtp_support` and holds 32 tensors in one layer. It
 * is a genuine model file and it is not *the* model, and no filename rule
 * distinguishes it from a quantization — `MTP` is as plausible a quant marker as
 * `MXFP4`.
 *
 * Returns a reason, or `undefined` when nothing about the file says it is a
 * sidecar. Advisory: the caller discloses it rather than refusing, because a
 * visitor who deep-linked to an MTP head may have meant to.
 */
export function auxiliaryReason(
  architecture: string | undefined,
  tensorCount: number,
  layerCount: number,
): string | undefined {
  if (architecture && /_(?:mtp_support|draft|eagle)$/i.test(architecture)) {
    return `declares itself \`${architecture}\` — a speculative-decoding sidecar, not the base model`;
  }
  /*
   * A whole model has a stack. One layer and a handful of tensors is a head, a
   * projector or a draft model — the threshold is deliberately far below any
   * real architecture rather than tuned to one file.
   */
  if (layerCount <= 1 && tensorCount < 64) {
    return `holds ${tensorCount} tensors across ${layerCount} layer(s) — too few to be a whole model`;
  }
  return undefined;
}
